import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { 
  Star, Milk, Scale, Calendar, Droplets, Heart, 
  ChevronRight, User, FileText, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animal type icons
const ANIMAL_ICONS = {
  goat: '🐐',
  sheep: '🐑',
  cattle: '🐄',
  chicken: '🐔',
  duck: '🦆',
  turkey: '🦃',
  goose: '🦢',
  quail: '🐦'
};

const StarAnimalsGallery = ({ getAuthHeader, onAnimalClick, sectionTitle = "Your Star Animals" }) => {
  const navigate = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [animalStats, setAnimalStats] = useState({});

  // Navigate to animal's full profile
  const navigateToAnimalProfile = (animal) => {
    setShowProfileDialog(false);
    
    // The herd/flock ID is stored as group_id in foundation animals
    const herdId = animal.group_id || animal.herd_id;
    const flockId = animal.flock_id || animal.group_id;
    
    // Determine if this is livestock or poultry based on animal_type
    const livestockTypes = ['goat', 'sheep', 'cattle'];
    const poultryTypes = ['chicken', 'duck', 'turkey', 'goose', 'quail', 'guinea', 'pigeon', 'poultry'];
    const animalType = animal.animal_type?.toLowerCase() || '';
    const isLivestock = livestockTypes.includes(animalType);
    const isPoultry = poultryTypes.includes(animalType);
    
    // Debug toast visible on mobile
    toast.info(`Type: ${animal.animal_type}, Poultry: ${isPoultry}, Livestock: ${isLivestock}`);
    
    // Check if we have a valid group ID
    const groupId = herdId || flockId;
    if (!groupId) {
      // No group ID - show helpful message and still navigate
      toast.error(`${animal.name} isn't linked to a herd/flock. Go to Livestock/Poultry to find them.`);
      if (isLivestock) {
        navigate('/app/livestock');
      } else {
        navigate('/app/poultry');
      }
      return;
    }
    
    // Store the animal ID to open when the tab loads
    const profileData = {
      animalId: animal.id,
      herdId: isLivestock ? groupId : null,
      flockId: isPoultry ? groupId : null,
      source: animal.source,
      animalType: animal.animal_type,
      isLivestock: isLivestock,
      isPoultry: isPoultry,
      timestamp: Date.now()
    };
    localStorage.setItem('openAnimalProfile', JSON.stringify(profileData));
    
    // Navigate to the appropriate tab based on animal type
    if (isLivestock) {
      navigate('/app/livestock');
    } else {
      navigate('/app/poultry');
    }
    
    toast.success(`Opening ${animal.name}'s profile...`);
  };

  useEffect(() => {
    fetchStarAnimals();
  }, []);

  const fetchStarAnimals = async () => {
    setLoading(true);
    let livestockAnimals = [];
    let poultryAnimals = [];
    
    try {
      // Fetch all foundation animals (livestock)
      const livestockRes = await axios.get(
        `${API_URL}/api/animals/foundation`,
        { headers: getAuthHeader() }
      );
      livestockAnimals = (livestockRes.data.animals || []).map(a => ({
        ...a,
        source: 'foundation'
      }));
    } catch (error) {
      console.log('Foundation animals fetch:', error.response?.status || error.message);
    }
    
    try {
      // Fetch individual animals (poultry prized animals)
      const poultryRes = await axios.get(
        `${API_URL}/api/animals/individual?animal_type=poultry`,
        { headers: getAuthHeader() }
      );
      poultryAnimals = (poultryRes.data.animals || []).map(a => ({
        ...a,
        source: 'individual'
      }));
    } catch (error) {
      console.log('Individual animals fetch:', error.response?.status || error.message);
    }
    
    // Combine, deduplicate by ID, filter to only Foundation-starred animals, and sort
    const seenIds = new Set();
    const allAnimals = [...livestockAnimals, ...poultryAnimals]
      .filter(a => {
        if (seenIds.has(a.id)) return false;
        seenIds.add(a.id);
        // Only show animals that are marked as foundation (or legacy ones without the flag)
        // is_foundation === true OR is_foundation is undefined (for backward compatibility)
        return a.is_foundation === true || a.is_foundation === undefined;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    
    setAnimals(allAnimals);
    
    // Fetch stats for each animal
    if (allAnimals.length > 0) {
      fetchAnimalStats(allAnimals);
    }
    
    setLoading(false);
  };

  const fetchAnimalStats = async (animalsList) => {
    const stats = {};
    
    for (const animal of animalsList) {
      try {
        if (animal.source === 'foundation') {
          // Fetch detailed animal data with milk stats
          const res = await axios.get(
            `${API_URL}/api/animals/foundation/${animal.id}`,
            { headers: getAuthHeader() }
          );
          stats[animal.id] = {
            milk_30d: res.data.milk_30d_total || 0,
            milk_lifetime: res.data.milk_lifetime_total || 0,
            events_count: res.data.events?.length || 0,
            last_event: res.data.events?.[0]?.date || null
          };
        }
      } catch (error) {
        // Silently fail for individual stats
      }
    }
    
    setAnimalStats(stats);
  };

  const handleAnimalClick = (animal) => {
    setSelectedAnimal(animal);
    setShowProfileDialog(true);
  };

  const getSexSymbol = (sex) => {
    if (sex === 'female' || sex === 'Female') return { symbol: '♀', color: 'text-pink-500' };
    if (sex === 'male' || sex === 'Male') return { symbol: '♂', color: 'text-blue-500' };
    return { symbol: '?', color: 'text-gray-400' };
  };

  const getAnimalAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const now = new Date();
    const months = Math.floor((now - birth) / (1000 * 60 * 60 * 24 * 30));
    if (months < 12) return `${months}mo`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return remainingMonths > 0 ? `${years}y ${remainingMonths}mo` : `${years}y`;
  };

  if (loading) {
    return (
      <div className="field-panel">
        <div className="py-6 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
          <p className="text-xs text-muted-foreground mt-2">Loading animals...</p>
        </div>
      </div>
    );
  }

  if (animals.length === 0) {
    return null; // Don't show the section if no foundation animals
  }

  return (
    <>
      <div className="space-y-3" data-testid="foundation-animals-gallery">
        <h2 className="section-header flex items-center gap-2">
          <Heart className="w-4 h-4 text-primary" />
          {sectionTitle}
          <span className="ml-auto text-xs font-normal">
            {animals.length} {animals.length === 1 ? 'animal' : 'animals'}
          </span>
        </h2>
        
        {/* Animal Bubbles - Horizontal scroll on mobile */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin" data-testid="foundation-animals-grid">
            {animals.map((animal) => {
              const sexInfo = getSexSymbol(animal.sex || animal.gender);
              const age = getAnimalAge(animal.birth_date);
              const stats = animalStats[animal.id];
              
              return (
                <button
                  key={animal.id}
                  onClick={() => handleAnimalClick(animal)}
                  className="flex flex-col items-center group transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 rounded-lg p-2 flex-shrink-0"
                  data-testid={`foundation-animal-bubble-${animal.id}`}
                >
                  {/* Bubble Photo Container */}
                  <div className="relative">
                    {animal.photo_url ? (
                      <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-primary/20 group-hover:border-primary/40 transition-colors">
                        <img 
                          src={animal.photo_url} 
                          alt={animal.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted/40 border-3 border-primary/20 flex items-center justify-center group-hover:border-primary/40 transition-colors">
                        <span className="text-2xl">
                          {ANIMAL_ICONS[animal.animal_type] || <User className="w-6 h-6 text-muted-foreground" />}
                        </span>
                      </div>
                    )}
                    
                    {/* Sex Badge */}
                    <span className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-card flex items-center justify-center text-xs font-bold ${sexInfo.color} border border-border`}>
                      {sexInfo.symbol}
                    </span>
                  </div>
                  
                  {/* Animal Name */}
                  <span className="mt-1.5 font-medium text-xs text-center max-w-[70px] truncate">
                    {animal.name}
                  </span>
                  
                  {/* Quick Info */}
                  <span className="text-[10px] text-muted-foreground">
                    {age || animal.breed?.split(' ')[0] || animal.animal_type}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

      {/* Quick Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-sm" data-testid="foundation-animal-profile-dialog">
          {selectedAnimal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  {selectedAnimal.photo_url ? (
                    <img 
                      src={selectedAnimal.photo_url} 
                      alt={selectedAnimal.name}
                      className="w-11 h-11 rounded-full object-cover border-2 border-primary/20"
                    />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-muted/50 flex items-center justify-center text-xl">
                      {ANIMAL_ICONS[selectedAnimal.animal_type] || '🐾'}
                    </div>
                  )}
                  <div>
                    <span className="flex items-center gap-2">
                      {selectedAnimal.name}
                      <span className={getSexSymbol(selectedAnimal.sex || selectedAnimal.gender).color}>
                        {getSexSymbol(selectedAnimal.sex || selectedAnimal.gender).symbol}
                      </span>
                    </span>
                    <p className="text-sm font-normal text-muted-foreground">
                      {selectedAnimal.breed || 'Unknown breed'}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Age */}
                  {selectedAnimal.birth_date && (
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                      <Calendar className="w-5 h-5 mx-auto text-primary mb-1" />
                      <p className="font-semibold">{getAnimalAge(selectedAnimal.birth_date)}</p>
                      <p className="text-xs text-muted-foreground">Age</p>
                    </div>
                  )}
                  
                  {/* Milk Stats (if female dairy) */}
                  {animalStats[selectedAnimal.id]?.milk_30d > 0 && (
                    <div className="p-3 rounded-lg bg-blue-50 text-center">
                      <Milk className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                      <p className="font-semibold text-blue-700">
                        {animalStats[selectedAnimal.id].milk_30d.toFixed(1)} gal
                      </p>
                      <p className="text-xs text-blue-600">30-Day Milk</p>
                    </div>
                  )}
                  
                  {/* Lifetime Milk */}
                  {animalStats[selectedAnimal.id]?.milk_lifetime > 0 && (
                    <div className="p-3 rounded-lg bg-blue-50 text-center">
                      <Droplets className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                      <p className="font-semibold text-blue-700">
                        {animalStats[selectedAnimal.id].milk_lifetime.toFixed(1)} gal
                      </p>
                      <p className="text-xs text-blue-600">Lifetime Milk</p>
                    </div>
                  )}
                  
                  {/* Events Count */}
                  {animalStats[selectedAnimal.id]?.events_count > 0 && (
                    <div className="p-3 rounded-lg bg-muted/30 text-center">
                      <Activity className="w-5 h-5 mx-auto text-primary mb-1" />
                      <p className="font-semibold">{animalStats[selectedAnimal.id].events_count}</p>
                      <p className="text-xs text-muted-foreground">Events</p>
                    </div>
                  )}
                </div>
                
                {/* Animal Info */}
                <div className="space-y-2 text-sm">
                  {selectedAnimal.tag_number && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tag #</span>
                      <span className="font-medium">{selectedAnimal.tag_number}</span>
                    </div>
                  )}
                  {selectedAnimal.purpose && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Purpose</span>
                      <span className="font-medium capitalize">{selectedAnimal.purpose}</span>
                    </div>
                  )}
                  {selectedAnimal.birth_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Born</span>
                      <span className="font-medium">
                        {new Date(selectedAnimal.birth_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Notes Preview */}
                {selectedAnimal.notes && (
                  <div className="p-3 rounded-lg bg-muted/20 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <FileText className="w-4 h-4" />
                      Notes
                    </div>
                    <p className="line-clamp-2">{selectedAnimal.notes}</p>
                  </div>
                )}
                
                {/* Action Button */}
                <Button 
                  className="w-full"
                  onClick={() => navigateToAnimalProfile(selectedAnimal)}
                  data-testid="view-full-profile-btn"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  View Full Profile
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StarAnimalsGallery;
